<ng-container *transloco="let t">
  <div
    id="verification-code-card"
    class="bg-white w-full max-w-full"
    [class]="embedded() ? '' : 'p-6 sm:p-10 rounded-2xl shadow-card sm:w-120'"
  >
    @if (showLogo()) {
      <div id="verification-code-logo" class="flex justify-center mb-4">
        <img [src]="logo" [alt]="logoAlt" class="h-8 sm:h-10" />
      </div>
    }

    @if (showTitle()) {
      <h1 id="verification-code-title" class="text-center font-dm text-2xl font-medium text-ld-primary mb-4">
        {{ t('mfaVerificationTitle') }}
      </h1>
    }

    <p id="verification-code-description" class="font-dm text-sm text-ld-neutral-500 mb-6">
      {{ t('mfaVerificationCodeSentTo') }}
      <span id="verification-code-contact" class="font-bold text-ld-neutral">{{ maskedContact() }}</span>
      {{ t('mfaVerificationPleaseInput') }}
    </p>

    <div id="verification-code-input-container" class="mb-4">
      <mat-form-field appearance="outline" class="w-full mfa-form-field verification-form-field">
        <mat-label>{{ t('mfaVerificationCodeLabel') }}</mat-label>
        <input
          matInput
          #codeInput
          id="verification-code-input"
          type="text"
          [value]="verificationCode()"
          (input)="onCodeInput($event)"
          (keydown.enter)="onVerify()"
          autocomplete="one-time-code"
          inputmode="numeric"
          maxlength="6"
          class="tracking-widest font-dm font-medium text-base"
        />
      </mat-form-field>
    </div>

    @if (errorMessage()) {
      <div
        id="verification-code-error"
        class="mb-4 sm:mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2"
      >
        <mat-icon class="text-red-600 shrink-0 text-xl">error_outline</mat-icon>
        <span class="text-ld-neutral text-sm font-sourcesans">{{ errorMessage() }}</span>
      </div>
    }

    @if (successMessage()) {
      <div
        id="verification-code-success"
        class="mb-4 sm:mb-6 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm font-sourcesans"
      >
        {{ successMessage() }}
      </div>
    }

    <div id="verification-code-resend-container" class="flex items-center gap-1 mb-4">
      <span class="font-dm text-sm text-ld-neutral">
        {{ t('mfaVerificationDidntGetCode') }}
      </span>
      <button
        id="verification-code-resend-button"
        type="button"
        class="bg-transparent border-0 text-ld-link cursor-pointer p-0 font-dm text-sm font-bold hover:text-ld-link-hover focus-visible:outline-2 focus-visible:outline-ld-primary focus-visible:outline-offset-2 focus-visible:rounded disabled:text-ld-neutral-400 disabled:cursor-not-allowed"
        [disabled]="resendLoading()"
        (click)="onResend()"
      >
        {{ resendLoading() ? t('commonMessagesSending') : t('mfaVerificationResend') }}
      </button>
    </div>

    @if (showRememberDevice()) {
      <div id="verification-code-remember-container" class="mb-4 flex items-center gap-2">
        <mat-checkbox
          id="verification-code-remember-checkbox"
          [checked]="rememberDevice()"
          (change)="onRememberDeviceChange($event.checked)"
          class="remember-device-checkbox mfa-checkbox-blue"
        >
          <span class="font-sourcesans text-sm text-ld-neutral">
            {{ t('mfaVerificationRememberDevice') }}
          </span>
        </mat-checkbox>
        <button
          id="verification-code-remember-info-btn"
          type="button"
          class="inline-flex items-center justify-center size-5 border-0 cursor-pointer p-0 shrink-0 bg-transparent transition-colors focus-visible:outline-2 focus-visible:outline-ld-primary focus-visible:outline-offset-2"
          [matTooltip]="isMobile() ? '' : rememberDeviceTooltip"
          matTooltipPosition="above"
          matTooltipClass="remember-device-tooltip"
          [attr.aria-label]="t('mfaVerificationRememberDeviceHeading')"
          (click)="isMobile() ? openRememberDeviceSheet() : null"
        >
          <mat-icon class="text-ld-link! text-xl! w-5! h-5! leading-none!">info_outline</mat-icon>
        </button>
      </div>
    }

    @if (showRememberDeviceSheet()) {
      <div
        id="remember-device-sheet-backdrop"
        class="fixed inset-0 bg-black/50 z-[60] flex items-end"
        role="dialog"
        aria-modal="true"
        (click)="closeRememberDeviceSheet()"
        (keydown.escape)="closeRememberDeviceSheet()"
      >
        <div
          id="remember-device-sheet"
          class="w-full bg-white rounded-t-2xl remember-device-sheet-slide-up max-h-[80vh] flex flex-col"
          style="padding-bottom: env(safe-area-inset-bottom, 0px)"
          role="document"
          (click)="$event.stopPropagation()"
          (keydown.escape)="closeRememberDeviceSheet()"
        >
          <div class="flex items-center justify-between px-4 py-3 bg-ld-primary rounded-t-2xl shrink-0">
            <span class="font-dm text-sm sm:text-base font-semibold text-white">
              {{ t('mfaVerificationRememberDeviceHeading') }}
            </span>
            <button
              id="remember-device-sheet-close-header"
              type="button"
              class="flex items-center justify-center size-8 bg-transparent border-none cursor-pointer text-white"
              [attr.aria-label]="t('commonAriaClose')"
              (click)="closeRememberDeviceSheet()"
            >
              <mat-icon>close</mat-icon>
            </button>
          </div>

          <div class="overflow-y-auto flex-1 px-5 py-4">
            <p class="font-dm text-sm text-ld-neutral-700 leading-relaxed">
              {{ rememberDeviceTooltip }}
            </p>
          </div>

          <div class="px-5 pb-5 pt-2 shrink-0">
            <button
              id="remember-device-sheet-close-btn"
              type="button"
              class="w-full inline-flex items-center justify-center bg-ld-primary text-white font-dm text-sm font-bold tracking-[0.5px] rounded-[20px] h-11 px-4 hover:bg-ld-neutral-700 cursor-pointer uppercase transition-colors"
              (click)="closeRememberDeviceSheet()"
            >
              {{ t('commonAriaClose') }}
            </button>
          </div>
        </div>
      </div>
    }

    @if (showFaqLink()) {
      <p id="verification-code-faq-text" class="text-left font-dm text-xs text-ld-neutral-500 mb-4">
        {{ t('mfaFaqText1') }}
        <a
          id="verification-code-faq-link"
          [routerLink]="faqLink()"
          target="_blank"
          class="text-ld-primary underline cursor-pointer hover:text-ld-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-ld-primary focus-visible:outline-offset-2 focus-visible:rounded"
        >
          {{ t('mfaFaqLink') }}
        </a>
        {{ t('mfaFaqText2') }}
      </p>
    }

    <div
      id="verification-code-actions"
      [class]="embedded() && isMobile() ? 'mt-2 flex flex-col gap-3' : 'mt-2 flex items-center justify-end gap-4'"
    >
      <button
        id="verification-code-verify-button"
        type="button"
        class="btn-primary-ld-purple disabled:opacity-50 disabled:cursor-not-allowed"
        [class.w-full]="embedded() && isMobile()"
        [disabled]="!canVerify"
        (click)="onVerify()"
      >
        {{ verifyLoading() ? t('mfaVerificationVerifying') : t('mfaVerificationVerify') }}
      </button>
      <button
        id="verification-code-cancel-button"
        type="button"
        [class]="
          embedded() && isMobile()
            ? 'btn-outline-ld-purple w-full'
            : 'bg-transparent border-0 text-ld-neutral-700 cursor-pointer p-0 font-dm text-sm font-bold uppercase tracking-wide hover:text-ld-neutral-900 focus-visible:outline-2 focus-visible:outline-ld-primary focus-visible:outline-offset-2 focus-visible:rounded'
        "
        (click)="onCancel()"
      >
        {{ t('commonButtonsCancel') }}
      </button>
    </div>
  </div>
</ng-container>
